import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  canWrite,
  currentMonthValue,
  departmentLabel,
  errorMessage,
  financeDelete,
  financeGet,
  financeSend,
  monthRange,
  num,
  PAYMENT_METHODS,
  SOURCE_LABEL,
  todayValue,
  useFinanceCodes,
  withCurrent,
  won,
  type FinanceCtx,
  type FinanceRecord,
} from '../financeApi';
import MemberPicker, { type DonorValue } from './MemberPicker';

type RecordType = '수입' | '지출';

const STATUS_OPTIONS: Record<RecordType, string[]> = {
  수입: ['확인완료', '확인대기'],
  지출: ['집행완료', '승인대기'],
};

type Form = {
  recordDate: string;
  category: string;
  amount: string;
  donor: DonorValue;
  anonymous: boolean;
  department: string;
  method: string;
  status: string;
  memo: string;
};

type Period = { mode: 'month'; month: string } | { mode: 'range'; from: string; to: string };

function periodParams(period: Period) {
  return period.mode === 'month' ? monthRange(period.month) : { from: period.from, to: period.to };
}

// 수입(헌금 등록·내역)과 지출 내역이 같이 쓰는 목록 + 개별 입력 화면.
// 수입은 헌금자(교적 연결/이름만/무명), 지출은 집행 부서를 기록한다.
export default function RecordsPage({ ctx, type }: { ctx: FinanceCtx; type: RecordType }) {
  const { incomeCategories, expenseCategories, departments } = useFinanceCodes(ctx);
  const categories = type === '수입' ? incomeCategories : expenseCategories;
  const writable = canWrite(ctx);

  const [period, setPeriod] = useState<Period>({ mode: 'month', month: currentMonthValue() });
  const [category, setCategory] = useState('');
  const [department, setDepartment] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<FinanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<FinanceRecord | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [savedCount, setSavedCount] = useState(0);

  const fetchRows = useCallback(() => {
    setLoading(true);
    setError(null);
    financeGet<FinanceRecord[]>(ctx, '', {
      type,
      ...periodParams(period),
      category: category || undefined,
      department: department || undefined,
      status: status || undefined,
      q: q.trim() || undefined,
    })
      .then((data) => setRows(data ?? []))
      .catch((err) => setError(errorMessage(err, '내역을 불러오지 못했습니다.')))
      .finally(() => setLoading(false));
  }, [ctx, type, period, category, department, status, q]);

  useEffect(() => {
    const t = window.setTimeout(fetchRows, q ? 300 : 0);
    return () => window.clearTimeout(t);
  }, [fetchRows, q]);

  const emptyForm = (): Form => ({
    recordDate: period.mode === 'month' && period.month !== currentMonthValue() ? `${period.month}-01` : todayValue(),
    category: categories[0] ?? '',
    amount: '',
    donor: { memberId: null, donorName: '' },
    anonymous: false,
    department: '',
    method: type === '수입' ? '현금' : '계좌이체',
    status: STATUS_OPTIONS[type][0],
    memo: '',
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormError(null);
    setSavedCount(0);
    setFormOpen(true);
  };

  const openEdit = (row: FinanceRecord) => {
    setEditing(row);
    setForm({
      recordDate: row.recordDate.slice(0, 10),
      category: row.category,
      amount: String(row.amount),
      donor: { memberId: row.memberId, donorName: row.anonymous ? '' : row.donorName },
      anonymous: Boolean(row.anonymous),
      department: row.department,
      method: row.method,
      status: row.status,
      memo: row.memo,
    });
    setFormError(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setForm(null);
    if (savedCount > 0) fetchRows();
  };

  const submit = (keepOpen: boolean) => {
    if (!form) return;
    const amount = Number.parseInt(form.amount.replace(/,/g, ''), 10);
    const categoryValue = form.category || categories[0] || '';
    if (!form.recordDate || !categoryValue || !(amount > 0)) {
      setFormError('날짜·항목·금액을 확인해 주세요.');
      return;
    }
    setSaving(true);
    setFormError(null);
    const payload = {
      type,
      recordDate: form.recordDate,
      category: categoryValue,
      amount,
      memberId: form.anonymous ? null : form.donor.memberId,
      donorName: form.anonymous ? '' : form.donor.donorName,
      anonymous: form.anonymous,
      department: form.department,
      method: form.method,
      status: form.status,
      memo: form.memo,
    };
    const request = editing
      ? financeSend(ctx, 'put', `/${editing.id}`, payload)
      : financeSend(ctx, 'post', '', payload);
    request
      .then(() => {
        if (keepOpen && !editing) {
          // 연속 입력: 날짜·항목·방법은 그대로 두고 금액·헌금자만 비운다.
          setSavedCount((c) => c + 1);
          setForm({ ...form, amount: '', donor: { memberId: null, donorName: '' }, anonymous: false, memo: '' });
          return;
        }
        setFormOpen(false);
        setEditing(null);
        setForm(null);
        fetchRows();
      })
      .catch((err) => setFormError(errorMessage(err, '저장하지 못했습니다.')))
      .finally(() => setSaving(false));
  };

  const remove = (row: FinanceRecord) => {
    const warn = row.expenseRequestId ? '\n지출 결의서로 지급된 내역입니다. 결의서는 지급 완료 상태로 남습니다.' : '';
    if (!window.confirm(`이 내역을 삭제할까요?${warn}`)) return;
    financeDelete(ctx, `/${row.id}`)
      .then(fetchRows)
      .catch((err) => window.alert(errorMessage(err, '삭제하지 못했습니다.')));
  };

  const toggleStatus = (row: FinanceRecord) => {
    const [done, waiting] = STATUS_OPTIONS[type];
    financeSend(ctx, 'put', `/${row.id}/status`, { status: row.status === done ? waiting : done })
      .then(fetchRows)
      .catch((err) => window.alert(errorMessage(err, '상태를 바꾸지 못했습니다.')));
  };

  const totals = useMemo(() => {
    const total = rows.reduce((s, r) => s + r.amount, 0);
    const byCategory = new Map<string, number>();
    rows.forEach((r) => byCategory.set(r.category, (byCategory.get(r.category) ?? 0) + r.amount));
    return {
      total,
      anonymous: rows.filter((r) => r.anonymous).reduce((s, r) => s + r.amount, 0),
      unlinked: rows.filter((r) => type === '수입' && !r.anonymous && !r.memberId && r.donorName).length,
      waiting: rows.filter((r) => r.status === STATUS_OPTIONS[type][1]).length,
      byCategory: [...byCategory.entries()].sort((a, b) => b[1] - a[1]),
    };
  }, [rows, type]);

  const isIncome = type === '수입';

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">{isIncome ? '헌금 등록·내역' : '지출 내역'}</h2>
          <p className="manage-panel-desc">
            {isIncome
              ? '헌금을 성도별로 개별 입력하고 항목별로 조회합니다. 무명 헌금은 따로 표시되어 영수증 합산에서 빠집니다.'
              : '집행된 지출을 부서·계정과목별로 조회합니다. 부서 청구 건은 지출 결의서에서 승인·지급하면 여기에 자동으로 기록됩니다.'}
          </p>
        </div>
        {writable && (
          <button type="button" className="manage-btn manage-btn--primary" onClick={openCreate}>
            + {isIncome ? '헌금 입력' : '지출 직접 입력'}
          </button>
        )}
      </div>

      <div className="manage-filterbar">
        <div className="manage-seg">
          <button type="button" className={`manage-seg-btn${period.mode === 'month' ? ' is-active' : ''}`}
            onClick={() => setPeriod({ mode: 'month', month: currentMonthValue() })}>월별</button>
          <button type="button" className={`manage-seg-btn${period.mode === 'range' ? ' is-active' : ''}`}
            onClick={() => setPeriod({ mode: 'range', ...monthRange(currentMonthValue()) })}>기간</button>
        </div>
        {period.mode === 'month' ? (
          <input type="month" value={period.month} onChange={(e) => e.target.value && setPeriod({ mode: 'month', month: e.target.value })} />
        ) : (
          <>
            <input type="date" value={period.from} onChange={(e) => setPeriod({ ...period, from: e.target.value })} />
            <span>~</span>
            <input type="date" value={period.to} onChange={(e) => setPeriod({ ...period, to: e.target.value })} />
          </>
        )}
        <select className="fin-select" value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">{isIncome ? '전체 헌금 항목' : '전체 계정과목'}</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {!isIncome && (
          <select className="fin-select" value={department} onChange={(e) => setDepartment(e.target.value)}>
            <option value="">전체 부서</option>
            <option value="공통">공통</option>
            {departments.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        )}
        <select className="fin-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">전체 상태</option>
          {STATUS_OPTIONS[type].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="manage-filterbar-search">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={isIncome ? '헌금자·메모 검색' : '메모 검색'} />
        </div>
      </div>

      {error && <div className="manage-state manage-state--error">{error}</div>}

      {!error && (
        <>
          <div className="fin-kpis fin-kpis--compact">
            <div className="fin-kpi">
              <span>조회 합계</span>
              <strong className={isIncome ? 'manage-amt-in' : 'manage-amt-out'}>{won(totals.total)}</strong>
              <span className="fin-delta">{rows.length}건</span>
            </div>
            {isIncome && (
              <div className="fin-kpi">
                <span>무명 헌금</span>
                <strong>{won(totals.anonymous)}</strong>
                <span className="fin-delta">영수증 합산 제외</span>
              </div>
            )}
            {isIncome && (
              <div className="fin-kpi">
                <span>교적 미연결</span>
                <strong>{totals.unlinked}건</strong>
                <span className="fin-delta">이름만 기록된 헌금</span>
              </div>
            )}
            <div className="fin-kpi">
              <span>{STATUS_OPTIONS[type][1]}</span>
              <strong>{totals.waiting}건</strong>
            </div>
          </div>

          {totals.byCategory.length > 0 && (
            <div className="fin-chips">
              {totals.byCategory.map(([name, amount]) => (
                <button type="button" key={name} className={`fin-chip${category === name ? ' is-active' : ''}`}
                  onClick={() => setCategory(category === name ? '' : name)}>
                  {name} <b>{num(amount)}</b>
                </button>
              ))}
            </div>
          )}

          <div className="manage-card-block">
            <div className="manage-table-wrap">
              <table className="manage-table">
                <thead>
                  <tr>
                    <th style={{ width: 96 }}>날짜</th>
                    {isIncome ? <th>헌금자</th> : <th style={{ width: 100 }}>부서</th>}
                    <th style={{ width: 110 }}>{isIncome ? '헌금 항목' : '계정과목'}</th>
                    {!isIncome && <th>내역</th>}
                    <th className="fin-num" style={{ width: 130 }}>금액</th>
                    <th style={{ width: 80 }}>방법</th>
                    <th style={{ width: 86 }}>상태</th>
                    {isIncome && <th>메모</th>}
                    {writable && <th style={{ width: 120 }}>관리</th>}
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={8} className="manage-table-empty">불러오는 중입니다...</td></tr>
                  )}
                  {!loading && rows.length === 0 && (
                    <tr><td colSpan={8} className="manage-table-empty">내역이 없습니다.</td></tr>
                  )}
                  {!loading && rows.map((row) => (
                    <tr key={row.id}>
                      <td className="manage-table-meta">{row.recordDate.slice(0, 10)}</td>
                      {isIncome ? (
                        <td>
                          {row.anonymous ? (
                            <span className="manage-badge manage-badge--gray">무명</span>
                          ) : row.memberId ? (
                            <b>{row.donorName}</b>
                          ) : row.donorName ? (
                            <span>{row.donorName} <span className="manage-badge manage-badge--amber" title="교적과 연결되지 않은 이름">미연결</span></span>
                          ) : (
                            <span className="fin-muted">-</span>
                          )}
                        </td>
                      ) : (
                        <td>{departmentLabel(row.department)}</td>
                      )}
                      <td><span className="manage-badge manage-badge--blue">{row.category}</span></td>
                      {!isIncome && (
                        <td>
                          {row.memo}
                          {row.expenseRequestId && <span className="manage-badge manage-badge--purple fin-ml">결의서</span>}
                        </td>
                      )}
                      <td className={`fin-num ${isIncome ? 'manage-amt-in' : 'manage-amt-out'}`}>{won(row.amount)}</td>
                      <td className="manage-table-meta">{row.method || '-'}</td>
                      <td>
                        <button type="button" disabled={!writable}
                          className={`manage-badge fin-status-btn ${row.status === STATUS_OPTIONS[type][0] ? 'manage-badge--green' : 'manage-badge--amber'}`}
                          title={writable ? '눌러서 상태 변경' : undefined}
                          onClick={() => toggleStatus(row)}>
                          {row.status}
                        </button>
                      </td>
                      {isIncome && (
                        <td className="manage-table-meta">
                          {row.memo}
                          {row.source !== 'manual' && <span className="fin-source">{SOURCE_LABEL[row.source] ?? row.source}</span>}
                        </td>
                      )}
                      {writable && (
                        <td>
                          <div className="manage-row-actions">
                            <button type="button" className="manage-btn manage-btn--sm" onClick={() => openEdit(row)}>수정</button>
                            <button type="button" className="manage-btn manage-btn--sm manage-btn--danger" onClick={() => remove(row)}>삭제</button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {formOpen && form && (
        <div className="manage-modal-overlay" onClick={closeForm}>
          <div className="manage-modal" onClick={(e) => e.stopPropagation()}>
            <div className="manage-modal-header">
              <h3>{editing ? '내역 수정' : isIncome ? '헌금 입력' : '지출 직접 입력'}</h3>
              <button type="button" className="manage-modal-close" onClick={closeForm}>×</button>
            </div>
            <div className="manage-modal-body">
              {savedCount > 0 && <div className="fin-inline-ok">{savedCount}건 저장했습니다. 이어서 입력하세요.</div>}
              <div className="manage-form manage-form--grid">
                <label className="manage-field">
                  <span>날짜</span>
                  <input type="date" value={form.recordDate} onChange={(e) => setForm({ ...form, recordDate: e.target.value })} />
                </label>
                <label className="manage-field">
                  <span>{isIncome ? '헌금 항목' : '계정과목'}</span>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    {withCurrent(categories, form.category).map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                {isIncome ? (
                  <div className="manage-field manage-field--full">
                    <span>헌금자</span>
                    {form.anonymous ? (
                      <div className="fin-picked"><span>무명 헌금</span></div>
                    ) : (
                      <MemberPicker key={savedCount} ctx={ctx} value={form.donor} onChange={(donor) => setForm({ ...form, donor })} autoFocus={!editing} />
                    )}
                    <label className="fin-check">
                      <input type="checkbox" checked={form.anonymous}
                        onChange={(e) => setForm({ ...form, anonymous: e.target.checked })} />
                      무명(익명) 헌금
                    </label>
                  </div>
                ) : (
                  <label className="manage-field">
                    <span>부서</span>
                    <select value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}>
                      <option value="">공통</option>
                      {withCurrent(departments, form.department).map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </label>
                )}
                <label className="manage-field">
                  <span>금액</span>
                  <input inputMode="numeric" value={form.amount} placeholder="원"
                    onChange={(e) => setForm({ ...form, amount: e.target.value.replace(/[^\d]/g, '') })} />
                  {form.amount && <small className="fin-hint">{won(Number(form.amount))}</small>}
                </label>
                <label className="manage-field">
                  <span>{isIncome ? '헌금 방법' : '지급 방법'}</span>
                  <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}>
                    <option value="">선택 안 함</option>
                    {withCurrent(PAYMENT_METHODS, form.method).map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </label>
                <label className="manage-field">
                  <span>상태</span>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    {withCurrent(STATUS_OPTIONS[type], form.status).map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <label className="manage-field manage-field--full">
                  <span>{isIncome ? '메모' : '내역(적요)'}</span>
                  <input value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} />
                </label>
              </div>
              {formError && <div className="fin-inline-error">{formError}</div>}
            </div>
            <div className="manage-modal-footer">
              <button type="button" className="manage-btn" onClick={closeForm}>{savedCount > 0 ? '닫기' : '취소'}</button>
              {!editing && (
                <button type="button" className="manage-btn" onClick={() => submit(true)} disabled={saving}>
                  저장 후 계속 입력
                </button>
              )}
              <button type="button" className="manage-btn manage-btn--primary" onClick={() => submit(false)} disabled={saving}>
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
